// ============================================
// WorkforceStatCard Component
// Large stat card with employee count and delta
// ============================================

import { ArrowUpIcon, ArrowDownIcon } from '../../../components/icons';
import { cn } from '../../../utils/cn';
import { useI18n } from '../../../i18n';

// ============================================
// Types
// ============================================

interface WorkforceStatCardProps {
  total: number;
  deltaPercent?: number | null;
  deltaDirection: 'up' | 'down';
  deltaLabel: string;
  isLoading?: boolean;
  className?: string;
}

// ============================================
// Component
// ============================================

export function WorkforceStatCard({
  total,
  deltaPercent,
  deltaDirection,
  deltaLabel,
  isLoading,
  className,
}: WorkforceStatCardProps) {
  const { t } = useI18n();
  if (isLoading) {
    return (
      <div
        className={cn(
          'relative h-36 overflow-hidden rounded-xl bg-white p-6 shadow-sm',
          className
        )}
      >
        <div className="h-3 w-32 animate-pulse rounded bg-slate-200" />
        <div className="mt-4 h-12 w-24 animate-pulse rounded bg-slate-200" />
        <div className="mt-3 h-3 w-40 animate-pulse rounded bg-slate-200" />
      </div>
    );
  }

  return (
    <div
      className={cn(
        'relative h-36 overflow-hidden rounded-xl bg-white p-6 shadow-sm',
        className
      )}
      style={{ boxShadow: '0px 1px 2px rgba(0,0,0,0.05)' }}
    >
      {/* Decorative people silhouettes */}
      <div className="pointer-events-none absolute bottom-0 right-6 h-32 w-32 opacity-10">
        <svg viewBox="0 0 128 128" className="h-full w-full text-slate-900">
          {/* Person 1 */}
          <circle cx="40" cy="24" r="12" fill="currentColor" />
          <path
            d="M20 128 L20 72 Q20 52 40 52 Q60 52 60 72 L60 128"
            fill="currentColor"
          />
          {/* Person 2 */}
          <circle cx="88" cy="24" r="12" fill="currentColor" />
          <path
            d="M68 128 L68 72 Q68 52 88 52 Q108 52 108 72 L108 128"
            fill="currentColor"
          />
        </svg>
      </div>

      {/* Content */}
      <div className="relative z-10">
        {/* Label */}
        <p className="text-xs font-bold uppercase tracking-[1.2px] text-primary-600">
          {t('activeWorkforce')}
        </p>

        {/* Value */}
        <p className="mt-2 font-sans text-5xl font-extrabold text-[#191B23]">
          {total.toLocaleString()}
        </p>

        {/* Delta */}
        {deltaPercent != null && (
          <div className="mt-2 flex items-center gap-1.5">
            {deltaDirection === 'up' ? (
              <ArrowUpIcon size={14} className="text-emerald-600" />
            ) : (
              <ArrowDownIcon size={14} className="text-red-600" />
            )}
            <span
              className={cn(
                'text-sm font-semibold',
                deltaDirection === 'up' ? 'text-emerald-600' : 'text-red-600'
              )}
            >
              {deltaPercent}% {deltaLabel}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
